import React, { useEffect, useState } from 'react';
import { catalog as catalogApi } from '@/utils/api';
import { Plus, Trash2, X, Edit2, BookOpen, Tag } from 'lucide-react';
import { toast } from 'sonner';

export default function CatalogPanel() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'general', default_price: 100, default_hours: 1, unit: 'hour', description: '', tags: '' });

  const reload = () => catalogApi.list({ q: q || undefined }).then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [q]);

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name, category: item.category || 'general',
      default_price: item.default_price || 0, default_hours: item.default_hours || 1,
      unit: item.unit || 'hour', description: item.description || '',
      tags: (item.tags || []).join(', '),
    });
  };

  const openNew = () => {
    setEditing({});
    setForm({ name: '', category: 'general', default_price: 100, default_hours: 1, unit: 'hour', description: '', tags: '' });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editing?.id) await catalogApi.update(editing.id, payload);
      else await catalogApi.create(payload);
      setEditing(null);
      reload();
      toast.success('Saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete catalog item?')) return;
    await catalogApi.delete(id);
    reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-bold" style={{ color: 'var(--ink)' }}>Task Catalog</h2>
        <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={openNew} data-testid="add-catalog-item-button">
          <Plus size={13} /> Add Task
        </button>
      </div>
      <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>Define reusable tasks with default pricing + category tags — pick them directly in estimates and invoices to stay consistent.</p>
      <input className="input-agency text-sm mb-3" placeholder="Search name / category / tag…" value={q} onChange={e => setQ(e.target.value)} data-testid="catalog-panel-search" />

      {items.length === 0 ? (
        <div className="card-base p-8 text-center">
          <BookOpen size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No catalog items</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.id} className="card-base p-3 flex items-center justify-between" data-testid={`catalog-row-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p className="font-bold text-sm truncate" style={{ color: 'var(--ink)' }}>{it.name}</p>
                  {it.category && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-sm flex items-center gap-0.5" style={{ background: '#E6F1F4', color: '#006080' }}>
                      <Tag size={8} /> {it.category}
                    </span>
                  )}
                </div>
                {it.description && <p className="text-xs truncate" style={{ color: 'var(--muted)' }}>{it.description}</p>}
                {it.tags?.length > 0 && (
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>tags: {it.tags.join(', ')}</p>
                )}
              </div>
              <div className="text-right flex-shrink-0 mx-4">
                <p className="text-sm font-bold tabular-nums" style={{ color: 'var(--ink)' }}>${it.default_price}</p>
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>/{it.unit || 'hour'}</p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <button onClick={() => openEdit(it)} className="p-1.5 rounded-btn" style={{ color: '#006080' }} data-testid={`catalog-edit-${it.id}`}><Edit2 size={13} /></button>
                <button onClick={() => remove(it.id)} className="p-1.5 rounded-btn" style={{ color: '#B34A3A' }} data-testid={`catalog-delete-${it.id}`}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>{editing.id ? 'Edit' : 'New'} Task</h3>
              <button onClick={() => setEditing(null)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Name *</label>
                <input className="input-agency text-sm" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="catalog-form-name" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Category</label>
                  <input className="input-agency text-sm" value={form.category} onChange={e => setForm({...form, category: e.target.value})} placeholder="RPA, n8n…" />
                </div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Price</label>
                  <input className="input-agency text-sm" type="number" value={form.default_price} onChange={e => setForm({...form, default_price: Number(e.target.value)})} />
                </div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Unit</label>
                  <select className="input-agency text-sm" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}>
                    <option value="hour">hour</option>
                    <option value="day">day</option>
                    <option value="fixed">fixed</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Description</label>
                <textarea className="input-agency text-sm resize-none" rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Tags (comma-separated)</label>
                <input className="input-agency text-sm" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="automation, scraping, python" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setEditing(null)}>Cancel</button>
                <button type="button" className="flex-1 btn-primary py-2 text-sm" onClick={save} disabled={!form.name.trim()} data-testid="catalog-save-submit">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
